// src/pages/finance-portal/loans/LoanForm.js
//
// Create / edit loan (full-page).
// Schedule auto-regenerates server-side whenever rate/term/method/dates change.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, TextField, IconButton, Card,
    MenuItem, Select, FormControl, InputLabel, Alert, CircularProgress,
    Skeleton, InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const TYPE_OPTIONS = [
    { value: 'TERM', label: 'Term Loan' },
    { value: 'REVOLVER', label: 'Revolver' },
    { value: 'LINE_OF_CREDIT', label: 'Line of Credit' },
    { value: 'MORTGAGE', label: 'Mortgage' },
    { value: 'EQUIPMENT', label: 'Equipment' },
    { value: 'BRIDGE', label: 'Bridge' },
    { value: 'OTHER', label: 'Other' },
];

const RATE_TYPE_OPTIONS = [
    { value: 'FIXED', label: 'Fixed' },
    { value: 'VARIABLE', label: 'Variable' },
];

const INTEREST_METHOD_OPTIONS = [
    { value: 'ACTUAL_365', label: 'Actual / 365' },
    { value: 'ACTUAL_360', label: 'Actual / 360' },
    { value: 'THIRTY_360', label: '30 / 360' },
    { value: 'COMPOUND_DAILY', label: 'Compound Daily' },
    { value: 'COMPOUND_MONTHLY', label: 'Compound Monthly' },
    { value: 'SIMPLE', label: 'Simple' },
];

const AMORT_METHOD_OPTIONS = [
    { value: 'FIXED_PAYMENT', label: 'Fixed Payment (P&I)' },
    { value: 'INTEREST_ONLY', label: 'Interest Only' },
    { value: 'BALLOON', label: 'Balloon' },
    { value: 'CUSTOM', label: 'Custom' },
];

const FREQUENCY_OPTIONS = [
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'SEMI_ANNUAL', label: 'Semi-Annual' },
    { value: 'ANNUAL', label: 'Annual' },
    { value: 'INTEREST_ONLY_MONTHLY', label: 'Interest-Only Monthly' },
    { value: 'BALLOON', label: 'Balloon' },
];

const initial = {
    name: '',
    description: '',
    lender: '',
    loan_type: 'TERM',
    currency: 'USD',
    original_amount: '',
    rate_type: 'FIXED',
    interest_rate: '',
    rate_index: '',
    rate_spread: '',
    interest_method: 'ACTUAL_365',
    amortization_method: 'FIXED_PAYMENT',
    payment_frequency: 'MONTHLY',
    term_months: 60,
    origination_date: '',
    first_payment_date: '',
    maturity_date: '',
    balloon_amount: '',
    collateral_description: '',
    notes: '',
};

export default function LoanForm() {
    const navigate = useNavigate();
    const { loanId } = useParams();
    const isEdit = !!loanId;
    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState({ ...initial });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const fetchLoan = useCallback(async () => {
        if (!isEdit) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/loans/${loanId}`);
            if (res.data.success) {
                const l = res.data.data;
                setData({
                    name: l.name || '',
                    description: l.description || '',
                    lender: l.lender || '',
                    loan_type: l.loan_type || 'TERM',
                    currency: l.currency || 'USD',
                    original_amount: String(l.original_amount ?? ''),
                    rate_type: l.rate_type || 'FIXED',
                    interest_rate: String(l.interest_rate ?? ''),
                    rate_index: l.rate_index || '',
                    rate_spread: l.rate_spread != null ? String(l.rate_spread) : '',
                    interest_method: l.interest_method || 'ACTUAL_365',
                    amortization_method: l.amortization_method || 'FIXED_PAYMENT',
                    payment_frequency: l.payment_frequency || 'MONTHLY',
                    term_months: l.term_months ?? 60,
                    origination_date: l.origination_date || '',
                    first_payment_date: l.first_payment_date || '',
                    maturity_date: l.maturity_date || '',
                    balloon_amount: l.balloon_amount != null ? String(l.balloon_amount) : '',
                    collateral_description: l.collateral_description || '',
                    notes: l.notes || '',
                });
                if (l.status === 'CLOSED') {
                    setError('This loan is closed and cannot be edited.');
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load loan');
        } finally {
            setLoading(false);
        }
    }, [isEdit, loanId]);

    useEffect(() => { fetchLoan(); }, [fetchLoan]);

    const update = (k, v) => {
        setData(prev => ({ ...prev, [k]: v }));
        if (errors[k]) setErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
    };

    const validateLocal = () => {
        const e = {};
        if (!data.name?.trim()) e.name = 'Required';
        if (!data.lender?.trim()) e.lender = 'Required';
        const amt = parseFloat(data.original_amount);
        if (isNaN(amt) || amt <= 0) e.original_amount = 'Must be > 0';
        const rate = parseFloat(data.interest_rate);
        if (isNaN(rate) || rate < 0) e.interest_rate = 'Must be ≥ 0';
        const term = parseInt(data.term_months, 10);
        if (isNaN(term) || term < 1) e.term_months = 'Required';
        if (!data.origination_date) e.origination_date = 'Required';
        if (!data.first_payment_date) e.first_payment_date = 'Required';
        if (!data.maturity_date) e.maturity_date = 'Required';
        if (data.first_payment_date && data.origination_date && data.first_payment_date < data.origination_date) {
            e.first_payment_date = 'Must be on or after origination';
        }
        if (data.maturity_date && data.first_payment_date && data.maturity_date < data.first_payment_date) {
            e.maturity_date = 'Must be on or after first payment';
        }
        if (data.amortization_method === 'BALLOON') {
            const bal = parseFloat(data.balloon_amount);
            if (isNaN(bal) || bal < 0) e.balloon_amount = 'Required for balloon';
        }
        return e;
    };

    const handleSave = async () => {
        const localErrors = validateLocal();
        if (Object.keys(localErrors).length > 0) {
            setErrors(localErrors);
            enqueueSnackbar('Fix the highlighted errors', { variant: 'warning' });
            return;
        }
        setSaving(true);
        setErrors({});
        try {
            const payload = {
                name: data.name.trim(),
                description: data.description?.trim() || null,
                lender: data.lender.trim(),
                loan_type: data.loan_type,
                currency: (data.currency || 'USD').toUpperCase(),
                original_amount: parseFloat(data.original_amount),
                rate_type: data.rate_type,
                interest_rate: parseFloat(data.interest_rate),
                rate_index: data.rate_index?.trim() || null,
                rate_spread: data.rate_spread === '' ? null : parseFloat(data.rate_spread),
                interest_method: data.interest_method,
                amortization_method: data.amortization_method,
                payment_frequency: data.payment_frequency,
                term_months: parseInt(data.term_months, 10),
                origination_date: data.origination_date,
                first_payment_date: data.first_payment_date,
                maturity_date: data.maturity_date,
                balloon_amount: data.balloon_amount === '' ? null : parseFloat(data.balloon_amount),
                collateral_description: data.collateral_description?.trim() || null,
                notes: data.notes?.trim() || null,
            };
            let id = loanId;
            if (isEdit) {
                await axiosInstance.put(`/api/finance/loans/${loanId}`, payload);
                enqueueSnackbar('Loan updated; schedule regenerated if terms changed', { variant: 'success' });
            } else {
                const res = await axiosInstance.post('/api/finance/loans', payload);
                id = res.data?.data?.id;
                enqueueSnackbar('Loan created with amortization schedule', { variant: 'success' });
            }
            navigate(`/finance-portal/loans/${id}`);
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                enqueueSnackbar('Validation failed', { variant: 'warning' });
            } else {
                enqueueSnackbar(err.response?.data?.message || 'Failed to save', { variant: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const fieldSx = { fontFamily, fontSize: '0.85rem', borderRadius: '10px' };
    const labelSx = { fontFamily, fontSize: '0.85rem' };

    if (loading) return <Box sx={{ p: 4, fontFamily }}><Skeleton height={400} /></Box>;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/loans')}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                            Loan & Debt Management
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 800, color: 'white' }}>
                            {isEdit ? 'Edit Loan' : 'New Loan'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1100, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                        Loan Identity
                    </Typography>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                        <TextField label="Name *" size="small"
                                   value={data.name}
                                   onChange={e => update('name', e.target.value)}
                                   error={!!errors.name} helperText={errors.name || ' '}
                                   InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                        <TextField label="Lender *" size="small"
                                   value={data.lender}
                                   onChange={e => update('lender', e.target.value)}
                                   error={!!errors.lender} helperText={errors.lender || ' '}
                                   InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>Loan Type</InputLabel>
                            <Select value={data.loan_type} label="Loan Type"
                                    onChange={e => update('loan_type', e.target.value)}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {TYPE_OPTIONS.map(o =>
                                    <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField label="Currency" size="small"
                                   value={data.currency}
                                   onChange={e => update('currency', e.target.value.toUpperCase())}
                                   inputProps={{ maxLength: 3 }}
                                   InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    </Box>
                    <TextField label="Description" size="small" fullWidth multiline rows={2} sx={{ mt: 1.5 }}
                               value={data.description}
                               onChange={e => update('description', e.target.value)}
                               InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                        Amounts & Rate
                    </Typography>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                        <TextField label="Original Amount *" size="small" type="number"
                                   value={data.original_amount}
                                   onChange={e => update('original_amount', e.target.value)}
                                   error={!!errors.original_amount}
                                   helperText={errors.original_amount || ' '}
                                   InputProps={{
                                       startAdornment: <InputAdornment position="start"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                       sx: fieldSx,
                                   }}
                                   InputLabelProps={{ sx: labelSx }} />
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>Rate Type</InputLabel>
                            <Select value={data.rate_type} label="Rate Type"
                                    onChange={e => update('rate_type', e.target.value)}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {RATE_TYPE_OPTIONS.map(o =>
                                    <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField label="Interest Rate (annual %) *" size="small" type="number"
                                   value={data.interest_rate}
                                   onChange={e => update('interest_rate', e.target.value)}
                                   error={!!errors.interest_rate}
                                   helperText={errors.interest_rate || 'e.g. 6.25'}
                                   InputProps={{
                                       endAdornment: <InputAdornment position="end"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>%</InputAdornment>,
                                       sx: fieldSx,
                                   }}
                                   InputLabelProps={{ sx: labelSx }} />
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>Interest Method</InputLabel>
                            <Select value={data.interest_method} label="Interest Method"
                                    onChange={e => update('interest_method', e.target.value)}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {INTEREST_METHOD_OPTIONS.map(o =>
                                    <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        {data.rate_type === 'VARIABLE' && (
                            <>
                                <TextField label="Rate Index (e.g. SOFR)" size="small"
                                           value={data.rate_index}
                                           onChange={e => update('rate_index', e.target.value)}
                                           InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                                <TextField label="Spread (%)" size="small" type="number"
                                           value={data.rate_spread}
                                           onChange={e => update('rate_spread', e.target.value)}
                                           InputProps={{
                                               endAdornment: <InputAdornment position="end"
                                                   sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>%</InputAdornment>,
                                               sx: fieldSx,
                                           }}
                                           InputLabelProps={{ sx: labelSx }} />
                            </>
                        )}
                    </Box>
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                        Schedule
                    </Typography>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>Amortization Method</InputLabel>
                            <Select value={data.amortization_method} label="Amortization Method"
                                    onChange={e => update('amortization_method', e.target.value)}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {AMORT_METHOD_OPTIONS.map(o =>
                                    <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>Payment Frequency</InputLabel>
                            <Select value={data.payment_frequency} label="Payment Frequency"
                                    onChange={e => update('payment_frequency', e.target.value)}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {FREQUENCY_OPTIONS.map(o =>
                                    <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField label="Term (months) *" size="small" type="number"
                                   value={data.term_months}
                                   onChange={e => update('term_months', e.target.value)}
                                   error={!!errors.term_months}
                                   helperText={errors.term_months || ' '}
                                   InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                        {data.amortization_method === 'BALLOON' && (
                            <TextField label="Balloon Amount" size="small" type="number"
                                       value={data.balloon_amount}
                                       onChange={e => update('balloon_amount', e.target.value)}
                                       error={!!errors.balloon_amount}
                                       helperText={errors.balloon_amount || 'Final principal due at maturity'}
                                       InputProps={{
                                           startAdornment: <InputAdornment position="start"
                                               sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                           sx: fieldSx,
                                       }}
                                       InputLabelProps={{ sx: labelSx }} />
                        )}
                        <TextField label="Origination Date *" type="date" size="small"
                                   value={data.origination_date}
                                   onChange={e => update('origination_date', e.target.value)}
                                   error={!!errors.origination_date}
                                   helperText={errors.origination_date || ' '}
                                   InputLabelProps={{ shrink: true, sx: labelSx }}
                                   InputProps={{ sx: fieldSx }} />
                        <TextField label="First Payment Date *" type="date" size="small"
                                   value={data.first_payment_date}
                                   onChange={e => update('first_payment_date', e.target.value)}
                                   error={!!errors.first_payment_date}
                                   helperText={errors.first_payment_date || ' '}
                                   InputLabelProps={{ shrink: true, sx: labelSx }}
                                   InputProps={{ sx: fieldSx }} />
                        <TextField label="Maturity Date *" type="date" size="small"
                                   value={data.maturity_date}
                                   onChange={e => update('maturity_date', e.target.value)}
                                   error={!!errors.maturity_date}
                                   helperText={errors.maturity_date || ' '}
                                   InputLabelProps={{ shrink: true, sx: labelSx }}
                                   InputProps={{ sx: fieldSx }} />
                    </Box>
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                        Collateral & Notes
                    </Typography>
                    <TextField label="Collateral" size="small" fullWidth multiline rows={2} sx={{ mb: 1.5 }}
                               value={data.collateral_description}
                               onChange={e => update('collateral_description', e.target.value)}
                               InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    <TextField label="Notes" size="small" fullWidth multiline rows={2}
                               value={data.notes}
                               onChange={e => update('notes', e.target.value)}
                               InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button onClick={() => navigate('/finance-portal/loans')}
                            sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving || !!error}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                  borderRadius: '10px', bgcolor: ACCENT,
                                  '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Loan'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
